import React from 'react';
import 'components/pagination/_pagination.scss';

const ELLIPSIS = 'ellipsis';

/**
 * Returns a limited set of page numbers to display (first, last, current ± siblings, with ellipsis for gaps).
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {number} siblingCount - how many page numbers to show on each side of current
 */
const getVisiblePages = (currentPage, totalPages, siblingCount = 1) => {
  if (totalPages <= 0) return [];
  if (totalPages === 1) return [1];

  const left = Math.max(1, currentPage - siblingCount);
  const right = Math.min(totalPages, currentPage + siblingCount);
  const result = [];

  if (left > 1) result.push(1);
  if (left > 2) result.push(ELLIPSIS);
  for (let i = left; i <= right; i++) result.push(i);
  if (right < totalPages - 1) result.push(ELLIPSIS);
  if (right < totalPages) result.push(totalPages);

  return result;
};

const Pagination = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange
}) => {
  const visiblePages = getVisiblePages(currentPage, totalPages);

  const handlePrev = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const handlePageClick = (page) => {
    if (page !== currentPage && typeof page === 'number') onPageChange(page);
  };

  return (
    <div className="pagination-container">
      <button className="pagination-btn" onClick={handlePrev} disabled={currentPage === 1} aria-label="Previous page">&lt;</button>
      {visiblePages.map((item, index) =>
        item === ELLIPSIS ? (
          <span key={`ellipsis-${index}`} className="pagination-ellipsis" aria-hidden="true">…</span>
        ) : (
          <button
            key={item}
            className={`pagination-btn${item === currentPage ? ' active' : ''}`}
            onClick={() => handlePageClick(item)}
            aria-label={item === currentPage ? `Page ${item} (current)` : `Go to page ${item}`}
            aria-current={item === currentPage ? 'page' : undefined}
          >
            {item}
          </button>
        )
      )}
      <button className="pagination-btn" onClick={handleNext} disabled={currentPage === totalPages} aria-label="Next page">&gt;</button>
      <span className="pagination-info">
        Showing
        <select
          className="pagination-select"
          value={itemsPerPage}
          onChange={e => onItemsPerPageChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        items out of {totalItems}
      </span>
    </div>
  );
};

export default Pagination; 